import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { motion } from 'framer-motion';
import { Briefcase, Building2, User, ChevronRight } from 'lucide-react';
import { OrgChartNodeData } from '../../types/org-chart';

const EmployeeNode = ({ data, selected }: NodeProps<OrgChartNodeData>) => {
  const statusColor = {
    active: 'bg-emerald-500',
    on_leave: 'bg-amber-500',
    offline: 'bg-slate-400',
    inactive: 'bg-rose-500',
    terminated: 'bg-slate-500',
  }[data.status || 'active'] || 'bg-slate-400';

  const [imgError, setImgError] = React.useState(false);

  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`relative group ${selected ? 'z-20' : 'z-10'}`}
    >
      {/* Selection Glow */}
      {selected && (
        <motion.div
          layoutId="pulse"
          className="absolute -inset-4 bg-primary-500/10 rounded-[2rem] -z-10 blur-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />
      )}

      <div className={`
        w-[300px] bg-white rounded-2xl border-2 transition-all duration-500
        ${selected
          ? 'border-primary-500 shadow-2xl scale-[1.02] ring-8 ring-primary-500/5'
          : 'border-slate-100 hover:border-primary-200 shadow-xl hover:shadow-2xl hover:-translate-y-1'}
      `}>
        {/* Dynamic Accent Bar */}
        <div className={`h-1.5 w-full rounded-t-[14px] ${selected ? 'bg-primary-500' : 'bg-slate-100 group-hover:bg-primary-300'} transition-colors duration-500`} />

        <div className="p-5">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-slate-50 overflow-hidden border-2 border-white shadow-inner rotate-3 group-hover:rotate-0 transition-transform duration-500">
                {data.profilePic && !imgError ? (
                  <img
                    src={data.profilePic}
                    alt={data.fullName}
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-primary-600 font-bold text-xl bg-slate-50">
                    {data.fullName?.charAt(0) || <User size={28} />}
                  </div>
                )}
              </div>
              {/* Status Indicator */}
              <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white shadow-sm ${statusColor}`} />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-0.5">
                <h3 className="text-sm font-bold text-slate-900 truncate tracking-tight">
                  {data.fullName}
                </h3>
                {selected && (
                  <motion.div
                    initial={{ x: -10, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                  >
                    <ChevronRight size={14} className="text-primary-500" />
                  </motion.div>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 truncate">
                <Briefcase size={12} className="shrink-0 text-slate-400" />
                <span className="truncate uppercase tracking-wider">{data.designation}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Meta */}
        <div className="px-5 py-3 bg-slate-50/80 backdrop-blur-sm border-t border-slate-100 rounded-b-[14px] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600 bg-white px-2.5 py-1 rounded-lg border border-slate-200/50 shadow-sm">
              <Building2 size={10} className="text-slate-400" />
              {data.department}
            </div>
          </div>
          <span className="text-[10px] text-slate-400 font-bold tracking-widest uppercase">
            {data.employeeId || 'USR-2026'}
          </span>
        </div>
      </div>

      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Top}
        className={`!w-3 !h-3 !bg-white !border-[2.5px] !border-primary-500 !-top-[7px] shadow-sm transition-all duration-300 ${data.isEditMode ? 'opacity-100 scale-100' : 'opacity-0 scale-100 pointer-events-none'}`}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className={`!w-3 !h-3 !bg-white !border-[2.5px] !border-primary-500 !bottom-0 shadow-sm transition-all duration-300 ${data.isEditMode ? 'opacity-100 scale-100' : 'opacity-0 scale-100 pointer-events-none'}`}
      />
    </motion.div>
  );
};

export default memo(EmployeeNode);
